/**
 * Creatine Upsell Popup
 * Shows popup when cart exceeds $60
 * Changes message if customer has "FREE-CREATINE" discount cookie
 *
 * Options:
 *   freeOnlyMode: true  - Only show popup when FREE-CREATINE cookie is present (default)
 *   freeOnlyMode: false - Show upsell popup to all customers over cart threshold
 *   cartThreshold: 60   - Cart total in dollars to trigger popup
 *   productHandle: 'fuze-creatine' - Product handle to add to cart
 *
 * Popup only shows once per browser (uses localStorage)
 */

class CreatineUpsellPopup {
  constructor(options = {}) {
    this.productHandle = options.productHandle || 'fuze-creatine';
    this.cartThreshold = options.cartThreshold || 60; // $60 minimum
    this.cookieName = options.cookieName || 'discount_code';
    this.discountCode = options.discountCode || 'FREE-CREATINE';
    this.popupShownKey = 'creatine_popup_shown';
    this.freeOnlyMode = true;

    // Setting: only show upsell when FREE-CREATINE coupon is active
    // When true, popup only appears if customer has the free coupon
    // When false, popup shows for all customers over cart threshold
    this.freeOnlyMode = options.freeOnlyMode !== undefined ? options.freeOnlyMode : true;

    this.init();
  }

  init() {
    // Create popup HTML
    this.createPopup();

    // Check cart on load
    this.checkCart();

    // Listen for cart updates
    document.addEventListener('cart:updated', () => this.checkCart());

    // Also listen for Shopify's native cart events
    if (window.Shopify && window.Shopify.onCartUpdate) {
      window.Shopify.onCartUpdate(() => this.checkCart());
    }
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  hasFreeCoupon() {
    const cookieValue = this.getCookie(this.cookieName);
    // console.log(cookieValue, " : ", this.discountCode.toLowerCase())
    return cookieValue.toLowerCase() === this.discountCode.toLowerCase();
  }

  createPopup() {
    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      .creatine-popup-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.6);
        display: none;
        justify-content: center;
        align-items: center;
        z-index: 9999;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      .creatine-popup-overlay.active {
        display: flex;
        opacity: 1;
      }

      .creatine-popup {
        background: #fff;
        border-radius: 12px;
        max-width: 420px;
        width: 90%;
        padding: 32px;
        position: relative;
        transform: scale(0.9);
        transition: transform 0.3s ease;
        text-align: center;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      }

      .creatine-popup-overlay.active .creatine-popup {
        transform: scale(1);
      }

      .creatine-popup-close {
        position: absolute;
        top: 12px;
        right: 12px;
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #666;
        line-height: 1;
        padding: 4px;
      }

      .creatine-popup-close:hover {
        color: #000;
      }

      .creatine-popup-icon {
        font-size: 48px;
        margin-bottom: 16px;
      }

      .creatine-popup-title {
        font-size: 24px;
        font-weight: 700;
        margin: 0 0 12px 0;
        color: #111;
      }

      .creatine-popup-title.free {
        color: #10b981;
      }

      .creatine-popup-message {
        font-size: 16px;
        color: #555;
        margin: 0 0 24px 0;
        line-height: 1.5;
      }

      .creatine-popup-btn {
        display: inline-block;
        background: #111;
        color: #fff;
        border: none;
        padding: 14px 32px;
        font-size: 16px;
        font-weight: 600;
        border-radius: 6px;
        cursor: pointer;
        transition: background 0.2s ease, transform 0.2s ease;
        width: 100%;
      }

      .creatine-popup-btn:hover {
        background: #333;
        transform: translateY(-1px);
      }

      .creatine-popup-btn:disabled {
        background: #999;
        cursor: not-allowed;
        transform: none;
      }

      .creatine-popup-btn.free {
        background: #10b981;
      }

      .creatine-popup-btn.free:hover {
        background: #059669;
      }

      .creatine-popup-btn.success {
        background: #10b981;
      }

      .creatine-popup-skip {
        background: none;
        border: none;
        color: #888;
        font-size: 14px;
        margin-top: 16px;
        cursor: pointer;
        text-decoration: underline;
      }

      .creatine-popup-skip:hover {
        color: #555;
      }
    `;
    document.head.appendChild(style);

    // Create popup HTML
    const popup = document.createElement('div');
    popup.className = 'creatine-popup-overlay';
    popup.id = 'creatine-upsell-popup';
    popup.innerHTML = `
      <div class="creatine-popup">
        <button class="creatine-popup-close" aria-label="Close">&times;</button>
        <div class="creatine-popup-icon" id="creatine-popup-icon"></div>
        <h2 class="creatine-popup-title" id="creatine-popup-title"></h2>
        <p class="creatine-popup-message" id="creatine-popup-message"></p>
        <button class="creatine-popup-btn" id="creatine-popup-btn"></button>
        <button class="creatine-popup-skip" id="creatine-popup-skip">No thanks</button>
      </div>
    `;
    document.body.appendChild(popup);

    // Event listeners
    popup.querySelector('.creatine-popup-close').addEventListener('click', () => this.close());
    popup.querySelector('.creatine-popup-skip').addEventListener('click', () => this.close());
    popup.addEventListener('click', (e) => {
      if (e.target === popup) this.close();
    });

    document.getElementById('creatine-popup-btn').addEventListener('click', () => this.addCreatine());

    this.popup = popup;
  }

  async checkCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Convert cents to dollars
      const cartTotal = cart.total_price / 100;

      // Check if creatine is already in cart
      const hasCreatine = cart.items.some(item => item.handle === this.productHandle);

      // Check if popup was already shown (persists across browser sessions)
      const alreadyShown = localStorage.getItem(this.popupShownKey);

      // Check if customer has the free coupon
      const hasFreeCreatine = this.hasFreeCoupon();

      // If freeOnlyMode is enabled, only show popup when coupon is active
      if (this.freeOnlyMode && !hasFreeCreatine) {
        return; // Don't show upsell popup without the free coupon
      }

      // Show popup if cart > $60, no creatine in cart, and not already shown
      if (cartTotal >= this.cartThreshold && !hasCreatine && !alreadyShown) {
        this.show();
      }
    } catch (error) {
      console.error('Error checking cart:', error);
    }
  }

  show() {
    const isFree = this.hasFreeCoupon();

    const icon = document.getElementById('creatine-popup-icon');
    const title = document.getElementById('creatine-popup-title');
    const message = document.getElementById('creatine-popup-message');
    const btn = document.getElementById('creatine-popup-btn');

    if (isFree) {
      icon.textContent = '🎉';
      title.textContent = 'FREE Creatine Unlocked!';
      title.classList.add('free');
      message.textContent = 'You\'ve unlocked a FREE tub of Fuze Creatine with your order! Add it to your cart now.';
      btn.textContent = 'Add FREE Creatine';
      btn.classList.add('free');
    } else {
      icon.textContent = '💪';
      title.textContent = 'Boost Your Gains!';
      title.classList.remove('free');
      message.textContent = 'Your cart qualifies for our premium Fuze Creatine. Add it now to maximize your results!';
      btn.textContent = 'Add Creatine to Cart';
      btn.classList.remove('free');
    }

    // Show popup with animation
    this.popup.style.display = 'flex';
    requestAnimationFrame(() => {
      this.popup.classList.add('active');
    });

    // Mark as shown (persists across browser sessions)
    localStorage.setItem(this.popupShownKey, 'true');
  }

  close() {
    this.popup.classList.remove('active');
    setTimeout(() => {
      this.popup.style.display = 'none';
    }, 300);
  }

  async addCreatine() {
    const btn = document.getElementById('creatine-popup-btn');
    const originalText = btn.textContent;

    try {
      btn.disabled = true;
      btn.textContent = 'Adding...';

      // Get product data
      const productResponse = await fetch(`/products/${this.productHandle}.js`);
      const product = await productResponse.json();

      if (!product || !product.variants || product.variants.length === 0) {
        throw new Error('Product not found');
      }

      // Get first available variant
      const variant = product.variants.find(v => v.available) || product.variants[0];

      // Add to cart
      const cartResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }]
        })
      });

      if (!cartResponse.ok) {
        throw new Error('Failed to add to cart');
      }

      // Success state
      btn.textContent = 'Added!';
      btn.classList.add('success');

      // Dispatch cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Close popup after delay
      setTimeout(() => {
        this.close();

        // Refresh cart drawer if it exists
        this.refreshCartDrawer();
      }, 1000);

    } catch (error) {
      console.error('Error adding creatine:', error);
      btn.textContent = 'Error - Try Again';
      btn.disabled = false;

      setTimeout(() => {
        btn.textContent = originalText;
      }, 2000);
    }
  }

  async refreshCartDrawer() {
    try {
      // Update cart count bubbles
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      document.querySelectorAll('.cart-count-bubble span').forEach(el => {
        el.textContent = cart.item_count;
      });

      // Refresh cart drawer section
      const sectionResponse = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-drawer`);
      const html = await sectionResponse.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCartDrawer = doc.querySelector('#CartDrawer');
      const currentCartDrawer = document.querySelector('#CartDrawer');

      if (newCartDrawer && currentCartDrawer) {
        currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }
}

// Initialize when DOM is ready
// To enable upsell for all customers (not just FREE-CREATINE), pass: { freeOnlyMode: false }
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CreatineUpsellPopup({ freeOnlyMode: true }));
} else {
  new CreatineUpsellPopup({ freeOnlyMode: true });
}
